import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { MANAGE_PATIENT_STIPEND_BULK_UPDATE } from 'constants/userOperations';
import { ROLE_PATIENT_LIAISON, ROLE_SITE_FINANCIAL_COORDINATOR, ROLE_TRAVEL_COORDINATOR } from 'constants/userRoles';
import { isEmpty } from 'lodash/lang';
import moment from 'moment';
import { userHasAccessTo, userHasRole } from 'services/auth';
import { read, utils } from 'xlsx';

import { StudySelector } from 'components/pages/pre-screen/PatientBulkUpdate/StudySelector/StudySelector';

import { StudySiteApi } from '../../../../api';
import PatientUpdateApi from '../../../../api/patient/PatientUpdateApi';
import DatePicker from '../../../../common/data-entry/DatePicker';
import Input from '../../../../common/data-entry/Input';
import Select from '../../../../common/data-entry/Select';
import Button from '../../../../common/general/Button';
import NotificationManager from '../../../../common/notifications/NotificationManager';
import { DD_SLASH_MMM_SLASH_YYYY } from '../../../../constants/dateFormat';
import { CLOSED, TREATMENT } from '../../../../constants/ssuStatuses';
import { isTodayOrBefore } from '../../../../services/date/validators';
import { onFileSave } from '../../../../services/handlers';
import { doesStringContainNullByte } from '../../../../services/string';
import { PageInfoHeader } from '../../../PageInfoHeader/PageInfoHeader';
import { SSUFilter } from '../../../SSUFilter/SSUFilter';
import { SSUPCNFilter } from '../../../SSUFilter/SSUPCNFilter';

import {
  ALLOWED_FORMATS,
  DESIRED_MAPPING,
  EXPECTED_KEYS_FOR_PATIENT_INFO_UPDATE,
  EXPECTED_KEYS_FOR_STIPEND_UPDATE,
  MRN_KEY,
  PATIENT_ID_KEY,
  REQUIRED_KEYS_FOR_PATIENT_INFO_UPDATE,
  REQUIRED_KEYS_FOR_STIPEND_UPDATE,
  SOURCE_NAME_KEY,
  SOURCE_TYPE_KEY,
  STATUSES_WITHOUT_COMMENT,
  STATUSES_WITHOUT_REASON,
  SUBJECT_ID_KEY,
  SURVEY_NUMBER_KEY,
  TAB_PATIENT_INFO_UPDATE,
  TAB_PROGRESS_NOTE_UPDATE,
  TAB_STATUS_UPDATE,
  TAB_STIPEND_UPDATE
} from './PatientBulkUpdateConstants';
import PatientBulkUpdateResult from './PatientBulkUpdateResult';

import './PatientBulkUpdate.scss';

export const PatientBulkUpdate = () => {
  const availableTabs = useMemo(() => {
    if (userHasRole([ROLE_SITE_FINANCIAL_COORDINATOR, ROLE_TRAVEL_COORDINATOR])) {
      return userHasAccessTo(MANAGE_PATIENT_STIPEND_BULK_UPDATE) ? [TAB_STIPEND_UPDATE] : [];
    } else if (userHasRole(ROLE_PATIENT_LIAISON)) {
      return [TAB_STATUS_UPDATE, TAB_PROGRESS_NOTE_UPDATE, TAB_PATIENT_INFO_UPDATE];
    } else {
      return [TAB_STATUS_UPDATE, TAB_PROGRESS_NOTE_UPDATE, TAB_PATIENT_INFO_UPDATE, TAB_STIPEND_UPDATE];
    }
  }, []);

  const [ssu, setSsu] = useState(null);
  const [study, setStudy] = useState(null);
  const hasFileBeenRead = useRef(false);
  const [file, setFile] = useState(null);
  const [patientIds, setPatientIds] = useState(null);
  const [patientInfoData, setPatientInfoData] = useState(null);
  const [stipendUpdateData, setStipendUpdateData] = useState(null);
  const [statuses, setStatuses] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [reasons, setReasons] = useState(null);
  const [selectedReason, setSelectedReason] = useState(null);
  const [comment, setComment] = useState(null);
  const [progressNote, setProgressNote] = useState(null);
  const [changeDate, setChangeDate] = useState(moment());
  const [fileData, setFileData] = useState(null);
  const [response, setResponse] = useState(null);
  const [errors, setErrors] = useState([]);
  const [activeTab, setActiveTab] = useState(availableTabs[0]);

  const validatePatientInfoUpdateTemplate = useCallback(
    (rawJson, expectedKeys, requiredFields) => {
      const columnNames = Object.keys(rawJson[0]);
      const oneOfRequiredFieldMissing = rawJson.some(row => {
        return requiredFields.some(field => !row[field]);
      });
      const listOfMissedHeaders = expectedKeys.filter(key => !columnNames.includes(key));

      if (activeTab === TAB_STIPEND_UPDATE && listOfMissedHeaders.length > 0) {
        NotificationManager.error(`File is missing required header(s): ${listOfMissedHeaders.join(', ')}`);
        setErrors(prevErrors => [...prevErrors, 'File']);
      } else if (listOfMissedHeaders.length > 0 || oneOfRequiredFieldMissing) {
        NotificationManager.error('File does not match required template, please use sample file for template');
        setErrors(prevErrors => [...prevErrors, 'File']);
      }
    },
    [activeTab]
  );

  const readFile = useCallback(
    (file, onRead) => {
      const reader = new FileReader();
      reader.onload = upload => {
        const data = upload.target.result?.split(',')[1];
        const workbook = read(data);
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        let rawJson;
        if (activeTab === TAB_PATIENT_INFO_UPDATE) {
          rawJson = utils
            .sheet_to_json(firstSheet, { blankrows: true, headers: 1, raw: false, defval: '' })
            .filter(row => Object.values(row).some(value => value !== ''));
          validatePatientInfoUpdateTemplate(
            rawJson,
            EXPECTED_KEYS_FOR_PATIENT_INFO_UPDATE,
            REQUIRED_KEYS_FOR_PATIENT_INFO_UPDATE
          );
        } else if (activeTab === TAB_STIPEND_UPDATE) {
          const DATA_ROW_START_OFFSET = 2;
          rawJson = utils
            .sheet_to_json(firstSheet, { blankrows: true, headers: 1, raw: false, defval: '' })
            .map((row, index) => ({ ...row, rowNumber: index + DATA_ROW_START_OFFSET }))
            .filter(row => Object.entries(row).some(([key, value]) => key !== 'rowNumber' && value !== ''));
          validatePatientInfoUpdateTemplate(
            rawJson,
            EXPECTED_KEYS_FOR_STIPEND_UPDATE,
            REQUIRED_KEYS_FOR_STIPEND_UPDATE
          );
        } else {
          rawJson = utils.sheet_to_json(firstSheet, { blankrows: true, headers: 1, raw: false });
        }

        onRead(data, rawJson);
      };
      reader.readAsDataURL(file);
    },
    [activeTab, validatePatientInfoUpdateTemplate]
  );

  const cleanHeaders = useCallback(
    json => {
      const normalizeKey = key => key.toLowerCase().replace(/_/g, ' ');
      const expectedKeys = Object.values(DESIRED_MAPPING).map(key => key.toLowerCase());

      const transformKeys = obj =>
        Object.entries(obj).reduce((acc, [key, value]) => {
          if (key.startsWith('__EMPTY') && activeTab !== TAB_STIPEND_UPDATE) {
            throw new Error('Missing header row');
          }

          const normalizedKey = normalizeKey(key);
          acc[DESIRED_MAPPING[normalizedKey] || key] = value;
          return acc;
        }, {});
      const cleaned = json.map(transformKeys);
      if (
        ![TAB_STIPEND_UPDATE, TAB_PATIENT_INFO_UPDATE].includes(activeTab) &&
        !cleaned.some(row => Object.keys(row).some(key => expectedKeys.includes(key.toLowerCase())))
      ) {
        throw new Error('Invalid header row');
      }
      return cleaned;
    },
    [activeTab]
  );

  const checkIfFileEmpty = useCallback(data => {
    return isEmpty(data);
  }, []);

  const addError = useCallback(
    errorType => {
      setErrors([...errors, errorType]);
    },
    [errors]
  );

  useEffect(() => {
    setSelectedStatus(null);
    if (ssu && ssu?.studyIdentifier && activeTab !== TAB_STIPEND_UPDATE) {
      PatientUpdateApi.getStatusesForBulkUpdate(ssu.studyIdentifier).then(({ data }) => {
        setStatuses(data);
      });
    }
  }, [activeTab, ssu]);

  const uploadDisabled = useMemo(() => {
    const studyOrSsuSelected = activeTab === TAB_STIPEND_UPDATE ? !isEmpty(study) : !isEmpty(ssu);
    return !studyOrSsuSelected || !(file instanceof File) || !isEmpty(errors);
  }, [activeTab, study, ssu, file, errors]);

  useEffect(() => {
    if (selectedStatus) {
      setReasons(selectedStatus.reasons);
    }
    if (isEmpty(selectedStatus?.reasons)) {
      setErrors(errors.filter(er => er !== 'Comment' && er !== 'Reason'));
      setComment(null);
    }
    setSelectedReason(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus]);

  useEffect(() => {
    if (file instanceof File && !hasFileBeenRead.current && ALLOWED_FORMATS.includes(file?.type)) {
      readFile(file, (data, rawJson) => {
        setFileData(data);
        if (checkIfFileEmpty(rawJson)) {
          NotificationManager.error(
            'The file could not be read; ensure there is a header row present and that the file is not empty, and try again'
          );
          addError('File');
          return;
        }

        let cleaned;
        try {
          cleaned = cleanHeaders(rawJson);
        } catch {
          NotificationManager.error('The file is missing a header row. Please include headers and try again.');
          addError('File');
          return;
        }

        if (cleaned && activeTab === TAB_STIPEND_UPDATE) {
          const trimmedData = cleaned.reduce((acc, row) => {
            if (!Object.keys(row).every(key => row[key] === '')) {
              const patientInfo = {
                surveyNumber: row[SURVEY_NUMBER_KEY]?.trim(),
                mrn: row[MRN_KEY]?.trim(),
                rowNumber: row.rowNumber
              };
              acc.push(patientInfo);
            }
            return acc;
          }, []);
          setStipendUpdateData(trimmedData);
        } else if (cleaned && activeTab === TAB_PATIENT_INFO_UPDATE) {
          const trimmedData = cleaned.reduce((acc, row) => {
            if (!Object.keys(row).every(key => row[key] === '')) {
              const patientInfo = {
                patientId: row[PATIENT_ID_KEY]?.trim(),
                mrn: row[MRN_KEY]?.trim(),
                subjectId: row[SUBJECT_ID_KEY]?.trim(),
                sourceType: row[SOURCE_TYPE_KEY]?.trim(),
                sourceName: row[SOURCE_NAME_KEY]?.trim()
              };
              acc.push(patientInfo);
            }
            return acc;
          }, []);
          setPatientInfoData(trimmedData);
        } else {
          const trimmedData = cleaned.reduce((acc, row) => {
            if (!Object.keys(row).every(key => row[key] === '')) {
              for (const [key, value] of Object.entries(row)) {
                if (key.toLowerCase().trim() !== 'patientid') {
                  continue;
                }
                acc.push(value.trim());
              }
            }
            return acc;
          }, []);
          setPatientIds(trimmedData);
        }
      });
      hasFileBeenRead.current = true;
    }
  }, [activeTab, addError, checkIfFileEmpty, cleanHeaders, file, readFile]);

  const loadSsuNotInTreatmentStatus = () => {
    return StudySiteApi.getAllStudySitesAndMap().then(({ data: ssus }) => {
      return ssus.filter(({ siteStatus }) => ![TREATMENT, CLOSED].includes(siteStatus));
    });
  };

  const selectSsu = ssus => {
    const selectedSSU = ssus.length === 1 ? ssus[0] : null;
    setSsu(selectedSSU);
  };
  const resetFileState = () => {
    setFile(null);
    setErrors(errors.filter(e => e !== 'File'));
    setFileData(null);
    setPatientIds(null);
  };

  const onChangeFileHandler = evt => {
    const file = evt.target.files[0];
    if (!file) {
      resetFileState();
      return;
    }

    if (!ALLOWED_FORMATS.includes(file?.type)) {
      NotificationManager.error('File is not an allowable type, only CSV, XLS or XLSX file types are allowed');
      addError('File');
    }

    if (doesStringContainNullByte(file?.name)) {
      NotificationManager.error('The file name format is unacceptable. Please rename the file and re-upload');
      resetFileState();
      return;
    }
    hasFileBeenRead.current = false;
    setFile(file);
  };

  const validateInputs = () => {
    if (activeTab === TAB_STATUS_UPDATE) {
      let validationErrors = [];
      isEmpty(ssu) || (isEmpty(ssu?.uniqueIdentifier) && validationErrors.push('Study Site'));
      isEmpty(selectedStatus) && validationErrors.push('Status');
      isEmpty(selectedReason) &&
        !STATUSES_WITHOUT_REASON.includes(selectedStatus?.id) &&
        validationErrors.push('Reason');
      isEmpty(comment?.trim()) &&
        (!STATUSES_WITHOUT_COMMENT.includes(selectedStatus?.id) ||
          (selectedStatus?.id === 'PRE-SCREEN FAILED' && selectedReason?.name === 'Other')) &&
        validationErrors.push('Comment');
      isEmpty(changeDate) && validationErrors.push('Date');
      setErrors(validationErrors);
      return isEmpty(validationErrors);
    } else if (activeTab === TAB_PROGRESS_NOTE_UPDATE) {
      let validationErrors = [];
      isEmpty(ssu) || (isEmpty(ssu?.uniqueIdentifier) && validationErrors.push('Study Site'));
      isEmpty(progressNote?.trim()) && validationErrors.push('Progress Note');
      isEmpty(changeDate) && validationErrors.push('Date');
      setErrors(validationErrors);
      return isEmpty(validationErrors);
    } else if (activeTab === TAB_STIPEND_UPDATE) {
      let validationErrors = [];
      !study.uniqueIdentifier && validationErrors.push('Study');
      setErrors(validationErrors);
      return isEmpty(validationErrors);
    } else {
      let validationErrors = [];
      isEmpty(ssu) || (isEmpty(ssu?.uniqueIdentifier) && validationErrors.push('Study Site'));
      setErrors(validationErrors);
      return isEmpty(validationErrors);
    }
  };

  const buildBulkStatusUpdateRequest = () => {
    if (STATUSES_WITHOUT_REASON.includes(selectedStatus?.id)) {
      return {
        ssuId: ssu.uniqueIdentifier,
        status: selectedStatus.id,
        comment: comment?.trim(),
        changeDate: changeDate.toISOString(),
        note: !isEmpty(progressNote?.trim()) ? progressNote?.trim() : null,
        patientIds,
        file: {
          type: file.type,
          fileUploadName: file.name,
          fileData: fileData
        }
      };
    } else {
      return {
        ssuId: ssu.uniqueIdentifier,
        status: selectedStatus.id,
        comment: comment?.trim(),
        changeDate: changeDate.toISOString(),
        reasonId: selectedReason.id,
        note: !isEmpty(progressNote?.trim()) ? progressNote?.trim() : null,
        patientIds,
        file: {
          type: file.type,
          fileUploadName: file.name,
          fileData: fileData
        }
      };
    }
  };

  const buildBulkProgressNoteRequest = () => {
    return {
      ssuId: ssu.uniqueIdentifier,
      noteCreatedDate: changeDate.toISOString(),
      note: progressNote.trim(),
      patientIds,
      file: {
        type: file.type,
        fileUploadName: file.name,
        fileData: fileData
      }
    };
  };

  const buildBulkPatientInfoRequest = () => {
    return {
      ssuId: ssu.uniqueIdentifier,
      patientInfoData,
      file: {
        type: file.type,
        fileUploadName: file.name,
        fileData: fileData
      }
    };
  };

  const stipedUpdateRequestData = useMemo(() => {
    return {
      stipendUpdateData,
      file: {
        type: file?.type,
        fileUploadName: file?.name,
        fileData
      }
    };
  }, [file, stipendUpdateData, fileData]);

  const processSubmit = () => {
    const isValid = validateInputs();
    if (isValid) {
      if (activeTab === TAB_STATUS_UPDATE) {
        PatientUpdateApi.bulkStatusUpdate(buildBulkStatusUpdateRequest()).then(({ data }) => setResponse(data));
      } else if (activeTab === TAB_PROGRESS_NOTE_UPDATE) {
        PatientUpdateApi.bulkAddProgressNote(buildBulkProgressNoteRequest()).then(({ data }) => setResponse(data));
      } else if (activeTab === TAB_STIPEND_UPDATE) {
        PatientUpdateApi.bulkStipendUpdate(stipedUpdateRequestData, study?.uniqueIdentifier).then(({ data }) =>
          setResponse(data)
        );
      } else {
        PatientUpdateApi.bulkPatientInfoUpdate(buildBulkPatientInfoRequest()).then(({ data }) => setResponse(data));
      }
    }
  };

  const downloadSample = () => {
    if (activeTab === TAB_STIPEND_UPDATE) {
      PatientUpdateApi.downloadStipendSampleFile().then(onFileSave);
    } else {
      PatientUpdateApi.downloadSampleFile().then(onFileSave);
    }
  };

  const isCommentRequired = useMemo(
    () =>
      selectedStatus?.id === 'CANCELED' ||
      (selectedStatus?.id === 'PRE-SCREEN FAILED' && selectedReason?.name === 'Other'),
    [selectedReason?.name, selectedStatus?.id]
  );

  return (
    <div>
      <PageInfoHeader>
        <div className="patient-bulk-update-section-header-tabs">
          <div className="patient-bulk-update-section-header-tabs-group">
            {availableTabs.map(function(tab) {
              return (
                <button
                  key={tab}
                  className={cx({ 'active-tab': activeTab === tab })}
                  onClick={() => {
                    if (tab !== activeTab) {
                      setActiveTab(tab);
                      setResponse(null);
                      setErrors([]);
                      resetFileState();
                      setStudy(null);
                    }
                  }}
                >
                  {tab}
                </button>
              );
            })}
          </div>
        </div>
        <>
          <div
            className={cx(
              'general-header-group-container',
              activeTab === TAB_STATUS_UPDATE ? 'more-than-five-fields-wrapper' : 'general-header-wrapper'
            )}
          >
            <SSUFilter handleSSUFilterChange={selectSsu} ssuProvider={loadSsuNotInTreatmentStatus}>
              {activeTab === TAB_STIPEND_UPDATE ? (
                <StudySelector changeStudy={study => setStudy(study)} selectedStudy={study} />
              ) : (
                <SSUPCNFilter isRequired={true} />
              )}
            </SSUFilter>
            {activeTab === TAB_STATUS_UPDATE && (
              <Select
                label="Status"
                optionLabelKey="name"
                optionValueKey="id"
                searchable
                required={true}
                dataSource={statuses}
                value={selectedStatus}
                onChange={status => {
                  setSelectedStatus(status);
                  setErrors(errors.filter(er => er !== 'Status'));
                }}
                validationMessage={errors.includes('Status') ? 'Status is not selected' : ''}
              />
            )}
            {![TAB_PATIENT_INFO_UPDATE, TAB_STIPEND_UPDATE].includes(activeTab) && (
              <DatePicker
                onChange={date => {
                  setChangeDate(date);
                  setErrors(errors.filter(er => er !== 'Date'));
                }}
                isValidDate={isTodayOrBefore}
                value={changeDate}
                label={'Date'}
                timeFormat={false}
                required
                closeOnSelect
                dateFormat={DD_SLASH_MMM_SLASH_YYYY}
                validationMessage={errors.includes('Date') ? 'Date is not selected' : ''}
              />
            )}
            {activeTab === TAB_STATUS_UPDATE && (
              <Select
                label="Reason"
                optionLabelKey="name"
                optionValueKey="id"
                searchable
                disabled={STATUSES_WITHOUT_REASON.includes(selectedStatus?.id)}
                required={true}
                dataSource={reasons}
                value={selectedReason}
                onChange={reason => {
                  setSelectedReason(reason);
                  setErrors(errors.filter(er => er !== 'Reason' && er !== 'Comment'));
                }}
                validationMessage={errors.includes('Reason') ? 'Reason is not selected' : ''}
              />
            )}
            {activeTab === TAB_STATUS_UPDATE && (
              <Input
                id={'comment-input'}
                name={'comment'}
                label={'Comment'}
                disabled={STATUSES_WITHOUT_REASON.includes(selectedStatus?.id)}
                required={isCommentRequired}
                value={comment}
                onChange={e => {
                  setComment(e.target.value);
                  setErrors(errors.filter(er => er !== 'Comment'));
                }}
                validationMessage={errors.includes('Comment') ? 'Comment is required' : ''}
              />
            )}
            {![TAB_PATIENT_INFO_UPDATE, TAB_STIPEND_UPDATE].includes(activeTab) && (
              <Input
                id={'progress-note-input'}
                name={'progress-note'}
                label={'Progress Note'}
                required={activeTab === TAB_PROGRESS_NOTE_UPDATE}
                value={progressNote}
                validationMessage={errors.includes('Progress Note') ? 'Progress Note is required' : ''}
                onChange={e => {
                  setProgressNote(e.target.value);
                  setErrors(errors.filter(er => er !== 'Progress Note'));
                }}
              />
            )}
            <Input.File
              name="patientsFileUpload"
              key={activeTab}
              fileName={file?.name || ''}
              onChangeFileHandler={onChangeFileHandler}
              acceptedUploadTypes={ALLOWED_FORMATS.join()}
              required
              validate
              validationMessage={errors.includes('File') ? 'Invalid file' : ''}
            />
            <Button disabled={uploadDisabled} onClick={() => processSubmit()} size="h56">
              Upload File
            </Button>
            {[TAB_PATIENT_INFO_UPDATE, TAB_STIPEND_UPDATE].includes(activeTab) && (
              <Button id="download-sample-file-button" className="load-btn" size="h56" onClick={downloadSample}>
                Sample File
              </Button>
            )}
          </div>
        </>
      </PageInfoHeader>
      {response && <PatientBulkUpdateResult response={response} activeTab={activeTab} />}
    </div>
  );
};
